/**
 * Motor "classificar" — arrastar para 2-3 caixas por criterio.
 *
 * D8: no maximo 3 caixas. O schema do motor nao aceita mais que isso.
 * D6: soltar na caixa "errada" NAO e erro. O item fica onde foi solto e a
 *     crianca pode mover de novo quantas vezes quiser. So conclui quando tudo
 *     estiver separado — sem penalidade, sem tentativa perdida, sem som de erro.
 */

import * as THREE from 'three';
import { BaseEngine } from './BaseEngine.js';

const CORES_CAIXA = [0xef9a9a, 0x90caf9, 0xc5e1a5];
const TAMANHO_ITEM = 0.44;

export class ClassificarEngine extends BaseEngine {
  constructor(ctx) {
    super(ctx);
    this.caixas = [];
    this.itens = [];
    this.grupo = null;
  }

  async iniciar(tarefa, banda) {
    await super.iniciar(tarefa, banda);

    const payload = tarefa.bandas[String(banda)];
    if (!payload) {
      throw new Error(`FAZENDA: a tarefa "${tarefa.id}" nao tem payload para a banda ${banda}.`);
    }

    this.numeroDeCaixas = Math.min(3, payload.caixas);
    this.negacao = payload.negacao === true;

    // As categorias vem do conteudo da tarefa, nunca de logica no JSON.
    const categorias = tarefa.conteudo?.categorias;
    if (!Array.isArray(categorias) || categorias.length < this.numeroDeCaixas) {
      throw new Error(
        `FAZENDA: a tarefa "${tarefa.id}" precisa de "conteudo.categorias" com pelo menos ` +
          `${this.numeroDeCaixas} categorias, uma por caixa. Confira o JSON da tarefa.`
      );
    }
    this.categorias = categorias.slice(0, this.numeroDeCaixas);

    this.montar();
    console.info(
      `FAZENDA: tarefa "${tarefa.id}" iniciada — banda ${banda}, ` +
        `${this.numeroDeCaixas} caixas (${this.categorias.join(', ')}).`
    );
  }

  montar() {
    const { fazenda, sceneManager, input } = this.ctx;

    this.grupo = new THREE.Group();
    this.grupo.name = 'tarefa-classificar';
    const base = fazenda.posicaoDaAncora(this.tarefa.ancora);
    this.alturaBase = base.y + 1.0;

    // Caixas, lado a lado na frente da ancora.
    const larguraTotal = (this.numeroDeCaixas - 1) * 2.4;
    this.categorias.forEach((categoria, i) => {
      const caixa = new THREE.Mesh(
        new THREE.BoxGeometry(1.9, 0.9, 1.5),
        new THREE.MeshStandardMaterial({ color: CORES_CAIXA[i], roughness: 1, flatShading: true })
      );
      caixa.position.set(base.x - larguraTotal / 2 + i * 2.4, base.y + 0.45, base.z + 2.8);
      caixa.userData.categoria = categoria;
      this.grupo.add(caixa);
      this.caixas.push(caixa);
    });

    // Itens para separar, embaralhados.
    const itens = embaralhar([...(this.tarefa.conteudo?.itens ?? [])]);
    if (itens.length === 0) {
      throw new Error(
        `FAZENDA: a tarefa "${this.tarefa.id}" nao tem "conteudo.itens" para separar. ` +
          'Cada item precisa de { "nome": ..., "categoria": ... }.'
      );
    }

    itens.forEach((item, i) => {
      const malha = new THREE.Mesh(
        new THREE.IcosahedronGeometry(TAMANHO_ITEM, 0),
        new THREE.MeshStandardMaterial({
          color: CORES_CAIXA[this.categorias.indexOf(item.categoria)] ?? 0xffffff,
          roughness: 0.9,
          flatShading: true,
        })
      );
      const porLinha = 4;
      malha.position.set(
        base.x - 1.8 + (i % porLinha) * 1.2,
        base.y + 0.5,
        base.z - 1.2 + Math.floor(i / porLinha) * 1.2
      );
      malha.userData.item = item;
      malha.userData.guardadoEm = null;

      this.grupo.add(malha);
      this.itens.push(malha);

      input.registrarArrastavel(malha, {
        destinos: () => this.caixas,
        aoSoltar: (objeto, caixa) => this.aoSoltarItem(objeto, caixa),
      });
    });

    sceneManager.adicionar(this.grupo);
    input.conferirTamanhoDeAlvo(this.itens[0], `item da tarefa ${this.tarefa.id}`);
  }

  aoSoltarItem(objeto, caixa) {
    const { audio } = this.ctx;

    if (!caixa) {
      // D6 — soltou no vazio. Nao aconteceu nada de ruim.
      audio.tocarTentarDeNovo();
      objeto.userData.guardadoEm = null;
      return;
    }

    objeto.userData.guardadoEm = caixa.userData.categoria;

    // Encaixa em cima da caixa, empilhando os que ja estao la.
    const jaNaCaixa = this.itens.filter(
      (i) => i !== objeto && i.userData.guardadoEm === caixa.userData.categoria
    ).length;

    objeto.position.set(
      caixa.position.x + ((jaNaCaixa % 3) - 1) * 0.5,
      this.alturaBase + 1.1 + Math.floor(jaNaCaixa / 3) * 0.4,
      caixa.position.z
    );

    audio.tocarToque(jaNaCaixa);

    if (this.concluiu()) {
      audio.tocarSucesso();
      for (const item of this.itens) this.ctx.input.desregistrar(item);
    }
  }

  /**
   * Conclui quando todo item esta na caixa da sua categoria.
   * Enquanto nao estiver, simplesmente continua — sem contagem de erro (D6).
   */
  concluiu() {
    return (
      this.itens.length > 0 &&
      this.itens.every((i) => i.userData.guardadoEm === i.userData.item.categoria)
    );
  }

  atualizar(dt) {
    // Os itens ainda soltos giram devagar, para chamar o dedo (D4).
    for (const item of this.itens) {
      if (!item.userData.guardadoEm) item.rotation.y += dt * 0.8;
    }
  }

  async concluir() {
    return super.concluir({ concluiu: this.concluiu() });
  }

  descartar() {
    const { input, sceneManager } = this.ctx;

    for (const item of this.itens) input.desregistrar(item);

    if (this.grupo) {
      sceneManager.remover(this.grupo);
      this.grupo.traverse((o) => {
        o.geometry?.dispose();
        o.material?.dispose();
      });
    }

    this.caixas = [];
    this.itens = [];
    this.grupo = null;
    super.descartar();
  }
}

function embaralhar(lista) {
  for (let i = lista.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [lista[i], lista[j]] = [lista[j], lista[i]];
  }
  return lista;
}
